import { useCallback, useMemo } from 'react';
import MapBaseLayer from '@alltrails/amplitude/enums/MapBaseLayer';
import MapOverlay from '@alltrails/amplitude/enums/MapOverlay';
import logMapBaseLayerTapped from '@alltrails/amplitude/events/logMapBaseLayerTapped';
import logMapOverlayTapped from '@alltrails/amplitude/events/logMapOverlayTapped';
import useAuthorization from '@alltrails/context/hooks/useAuthorization';
import { hasFeatureEntitlement } from '@alltrails/context/hooks/useFeatureEntitlements';
import useUser from '@alltrails/context/hooks/useUser';
import { useClientNavigationPathname } from '@alltrails/core/hooks/useClientNavigation';
import type { FeatureEntitlement } from '@alltrails/types/FeatureEntitlement';
import { useAppDispatch, useAppSelector } from '../redux';
import { setIsRouteBuildingEnabled } from '../redux/customRoute';
import {
  overlaysWithPopups,
  toggleDynamicStyleId,
  toggleStaticStyleId,
  updateAdminStyleSettings,
  updateBaseStyleId,
  updateDynamicStyleIds
} from '../redux/reducer';
import { HandleStyleRoadblock } from '../types/Roadblock';
import { BaseStyleId, BoundaryFilterType, DynamicStyleId, StyleId } from '../types/Styles';
import adminUrlIsValid from '../utils/adminUrlIsValid';
import { getMapBaseLayer, getMapDrawerLocation, getMapOverlay, getPage } from '../utils/amplitudeHelpers';
import { conditionsStyleIds } from '../utils/constants';
import { baseStyleCardConfigs, dynamicStyleCardConfigs, staticStyleCardConfigs } from '../utils/styleCardConfigs';
import { isBaseStyle, isDynamicStyle, isStaticStyle } from '../utils/styleIdHelpers';

// With the new layer menu, users can only have one of the single select ids applied at a time
const singleSelectDynamicIds: DynamicStyleId[] = [
  'newHeatmap',
  'personalHeatmap',
  ...conditionsStyleIds,
  'publicLands',
  'privateLands',
  'fallFoliage',
  'wildfire',
  'slopeAngle',
  'highDefTerrain'
];

type UseStyleSelectionArgs = {
  handleFailedEntitlementCheck: (entitlement: FeatureEntitlement, applyStyle: () => void, styleId?: DynamicStyleId | BaseStyleId) => void;
  handleRoadblock: HandleStyleRoadblock;
  onMapBaseLayerTapped?: (baseLayer: MapBaseLayer | undefined) => void;
  onMapOverlayTapped?: (overlay: MapOverlay | undefined, isAddition: boolean) => void;
  disableAnalytics?: boolean;
};

const useStyleSelection = ({
  handleFailedEntitlementCheck,
  onMapBaseLayerTapped,
  onMapOverlayTapped,
  disableAnalytics,
  handleRoadblock
}: UseStyleSelectionArgs) => {
  const dispatch = useAppDispatch();
  const { hasPermission } = useAuthorization();
  const user = useUser();
  const pathname = useClientNavigationPathname();
  const isAdmin = hasPermission({ permission: 'trails:manage' });
  const isPLP = hasPermission({ permission: 'public_lands:manage' });

  const { adminStyleSettings, baseStyleId, dynamicStyleIds, staticStyleIds, isCustomRouteEditing } = useAppSelector(state => ({
    adminStyleSettings: state.map.adminStyleSettings,
    baseStyleId: state.map.baseStyleId,
    dynamicStyleIds: state.map.dynamicStyleIds,
    staticStyleIds: state.map.staticStyleIds,
    is3dActive: state.map.is3dActive,
    // Optional: the customRoute slice isn't loaded in every app that uses this hook.
    isCustomRouteEditing: state.customRoute?.isEditing ?? false
  }));
  const page = getPage(pathname);

  const onStyleSelect = useCallback(
    (id: StyleId) => {
      if (isBaseStyle(id)) {
        if (id === baseStyleId) {
          // Do nothing if base style has already been selected
          return;
        }
        if (baseStyleCardConfigs[id].requiresAdminStyleSourceUrl && !adminUrlIsValid(adminStyleSettings?.styleSourceUrls?.[id])) {
          return;
        }

        if (!user) {
          handleRoadblock(id);
          return;
        }

        const mapBaseLayer = getMapBaseLayer(id);
        const config = baseStyleCardConfigs[id];

        if (mapBaseLayer && !disableAnalytics) {
          logMapBaseLayerTapped({ map_base_layer: mapBaseLayer, map_drawer_location: 'base map', page });
        }

        if (mapBaseLayer && onMapBaseLayerTapped) {
          onMapBaseLayerTapped(mapBaseLayer);
        }

        const updateBaseStyle = () => {
          dispatch(updateBaseStyleId(id));
        };

        if (config.featureEntitlement && !hasFeatureEntitlement(user, config.featureEntitlement)) {
          // User is trying to apply a base map that they don't have access to
          handleFailedEntitlementCheck(config.featureEntitlement, updateBaseStyle, id);
        } else {
          updateBaseStyle();
        }
      } else if (
        isStaticStyle(id) &&
        staticStyleCardConfigs[id].requiresAdminStyleSourceUrl &&
        !adminUrlIsValid(adminStyleSettings?.styleSourceUrls?.[id])
      ) {
        return;
      } else {
        if (isStaticStyle(id)) {
          if (!user) {
            handleRoadblock(id);
            return;
          }

          if (!disableAnalytics) {
            logMapOverlayTapped({
              is_addition: !staticStyleIds.includes(id),
              map_location: page,
              map_drawer_location: getMapDrawerLocation(id),
              map_overlay: getMapOverlay(id) ?? ('' as MapOverlay)
            });
          }

          if (onMapOverlayTapped) {
            onMapOverlayTapped(getMapOverlay(id), !staticStyleIds.includes(id));
          }
          dispatch(toggleStaticStyleId(id));
        } else if (isDynamicStyle(id)) {
          const config = dynamicStyleCardConfigs[id];

          if (!user) {
            handleRoadblock(id);
            return;
          }

          const isAddition = !dynamicStyleIds.includes(id);

          // Apply default boundary filters when enabling geoBoundaries for the first time
          if (id === 'geoBoundaries' && isAddition) {
            const needsDefaultFilters = !adminStyleSettings?.boundaryFilters || adminStyleSettings.boundaryFilters.length === 0;
            if (needsDefaultFilters && (isAdmin || isPLP)) {
              dispatch(
                updateAdminStyleSettings({
                  ...adminStyleSettings,
                  boundaryFilters: [BoundaryFilterType.alertClosure, BoundaryFilterType.alertNonClosure]
                })
              );
            }
          }

          if (!disableAnalytics) {
            logMapOverlayTapped({
              is_addition: isAddition,
              map_location: page,
              map_drawer_location: getMapDrawerLocation(id),
              map_overlay: getMapOverlay(id) ?? ('' as MapOverlay)
            });

            if (isAddition) {
              window.Sprig?.('identifyAndTrack', {
                eventName: 'Map_Overlay_Tapped',
                properties: {
                  is_addition: isAddition,
                  map_location: page,
                  map_drawer_location: getMapDrawerLocation(id),
                  map_overlay: getMapOverlay(id) ?? ('' as MapOverlay)
                }
              });
            }
          }

          if (onMapOverlayTapped) {
            onMapOverlayTapped(getMapOverlay(id), isAddition);
          }

          const updateStyles = () => {
            const idIsSingleSelect = singleSelectDynamicIds.includes(id);
            const idHasPopup = overlaysWithPopups.includes(id);
            // Route building only applies while editing a custom route. Only adjust it when the user
            // is actually in custom-route edit mode, otherwise toggling a popup overlay on a regular
            // explore/BYOT page would mutate (and persist) global customRoute state.
            if (isCustomRouteEditing && idHasPopup) {
              if (isAddition) {
                // A popup overlay intercepts map clicks, so pause route building while it's enabled.
                dispatch(setIsRouteBuildingEnabled(false));
              } else {
                // These are currently single-select, but this protects us when we allow multiple popup overlays to be applied at once in the future
                const otherPopupOverlayActive = overlaysWithPopups.some(overlayId => overlayId !== id && dynamicStyleIds.includes(overlayId));
                if (!otherPopupOverlayActive) {
                  dispatch(setIsRouteBuildingEnabled(true));
                }
              }
            }
            if (idIsSingleSelect && isAddition) {
              // We are enabling a single select id, so remove any already-applied single select ids
              const nonRestrictedDynamicIds = dynamicStyleIds.filter(id => !singleSelectDynamicIds.includes(id));
              dispatch(updateDynamicStyleIds([...nonRestrictedDynamicIds, id]));
            } else {
              dispatch(toggleDynamicStyleId(id));
            }
          };

          if (isAddition && config.featureEntitlement && !hasFeatureEntitlement(user, config.featureEntitlement)) {
            // User in peak experiment is trying to apply a style that they don't have access to
            handleFailedEntitlementCheck(config.featureEntitlement, updateStyles, id);
          } else {
            updateStyles();
          }
        }
      }
    },
    [
      adminStyleSettings,
      baseStyleId,
      user,
      disableAnalytics,
      onMapBaseLayerTapped,
      dispatch,
      handleRoadblock,
      isAdmin,
      isPLP,
      page,
      onMapOverlayTapped,
      staticStyleIds,
      dynamicStyleIds,
      handleFailedEntitlementCheck,
      isCustomRouteEditing
    ]
  );

  return useMemo(
    () => ({
      onStyleSelect
    }),
    [onStyleSelect]
  );
};

export default useStyleSelection;
